import React, { useEffect, useRef, useState } from "react";
import QmsModalPortal from "../qms/QmsModalPortal";
import { isDuplicateWorksheetName } from "./scheduleWorksheetUtils";
export const SCHEDULE_DOC_ALLOWED_EXTENSIONS = ["pdf", "xls", "xlsx"];
export const SCHEDULE_DOC_MAX_FILE_SIZE_MB = 10;

const FILE_ACCEPT_ATTRIBUTE = [
  ...SCHEDULE_DOC_ALLOWED_EXTENSIONS.map((ext) => `.${ext}`),
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
].join(",");

function getFileExtension(fileName) {
  const name = String(fileName || "").trim();
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) return "";
  return name.slice(lastDot + 1).toLowerCase();
}

function stripExtension(fileName) {
  const name = String(fileName || "").trim();
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0) return name;
  return name.slice(0, lastDot);
}

function isAllowedScheduleDocument(file) {
  if (!file) return false;
  return SCHEDULE_DOC_ALLOWED_EXTENSIONS.includes(getFileExtension(file.name));
}

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function validateSelectedFile(file) {
  if (!file) return "Please choose a file to upload.";
  if (!isAllowedScheduleDocument(file)) {
    return `Unsupported file type. Accepted formats: ${SCHEDULE_DOC_ALLOWED_EXTENSIONS.join(", ")}.`;
  }
  const sizeMb = file.size / (1024 * 1024);
  if (sizeMb > SCHEDULE_DOC_MAX_FILE_SIZE_MB) {
    return `File size exceeds the ${SCHEDULE_DOC_MAX_FILE_SIZE_MB} MB limit.`;
  }
  return "";
}

export default function ScheduleDocumentUploadModal({
  isOpen,
  onClose,
  onSave,
  saving = false,
  mode = "add",
  replaceTarget = null,
  existingRows = [],
}) {
  const fileInputRef = useRef(null);
  const nameTouchedRef = useRef(false);
  const [workSheetName, setWorkSheetName] = useState("");
  const [file, setFile] = useState(null);
  const [formError, setFormError] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const isReplace = mode === "replace";

  useEffect(() => {
    if (!isOpen) return;
    nameTouchedRef.current = false;
    setFile(null);
    setFormError("");
    setDragActive(false);
    if (isReplace && replaceTarget?.workSheetName) {
      setWorkSheetName(stripExtension(replaceTarget.workSheetName));
    } else {
      setWorkSheetName("");
    }
  }, [isOpen, isReplace, replaceTarget]);

  if (!isOpen) return null;

  const applySelectedFile = (nextFile) => {
    if (!nextFile) return;
    const validationError = validateSelectedFile(nextFile);
    if (validationError) {
      setFile(null);
      setFormError(validationError);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFile(nextFile);
    setFormError("");
    if (!nameTouchedRef.current) {
      const nextName = stripExtension(nextFile.name);
      setWorkSheetName(nextName);
      if (
        isDuplicateWorksheetName(nextName, existingRows, {
          excludeStudyWorkSheetId: isReplace ? replaceTarget?.studyWorkSheetId || null : null,
          excludeStudyWorkSheetGroupId: isReplace
            ? replaceTarget?.studyWorkSheetGroupId || null
            : null,
        })
      ) {
        setFormError("A document with this name already exists for this visit.");
      }
    }
  };

  const handleFileChange = (event) => {
    applySelectedFile(event.target.files?.[0] || null);
    if (event.target.files?.[0] && !isAllowedScheduleDocument(event.target.files[0])) {
      event.target.value = "";
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!saving) setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (saving) return;
    applySelectedFile(event.dataTransfer.files?.[0] || null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedName = workSheetName.trim();
    if (!trimmedName) {
      setFormError("Enter document name.");
      return;
    }
    const validationError = validateSelectedFile(file);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    if (
      isDuplicateWorksheetName(trimmedName, existingRows, {
        excludeStudyWorkSheetId: isReplace ? replaceTarget?.studyWorkSheetId || null : null,
        excludeStudyWorkSheetGroupId: isReplace
          ? replaceTarget?.studyWorkSheetGroupId || null
          : null,
      })
    ) {
      setFormError("A document with this name already exists for this visit.");
      return;
    }
    setFormError("");
    onSave?.({
      workSheetName: trimmedName,
      file,
      documentName: file.name,
      fileExtension: getFileExtension(file.name),
      studyWorkSheetId: isReplace ? replaceTarget?.studyWorkSheetId || null : null,
    });
  };

  const title = isReplace ? "Replace schedule document" : "Add schedule document";
  const subtitle = isReplace
    ? `Upload a newer version for ${replaceTarget?.workSheetName || "this document"} (currently v${replaceTarget?.version || 1}).`
    : "Upload a PDF or Excel worksheet for this visit.";

  return (
    <QmsModalPortal>
      <div
        className="modal fade show d-block gvisit-doc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gvisit-doc-modal-title"
        onClick={() => {
          if (!saving) onClose?.();
        }}
      >
        <div
          className="modal-dialog gvisit-doc-modal__dialog"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="modal-content gvisit-doc-modal__content">
            <div className="gvisit-doc-modal__header">
              <div className="gvisit-doc-modal__header-main">
                <div className="gvisit-doc-modal__icon" aria-hidden="true">
                  <i className={`fas ${isReplace ? "fa-sync-alt" : "fa-cloud-upload-alt"}`} />
                </div>
                <div>
                  <h5 className="gvisit-doc-modal__title" id="gvisit-doc-modal-title">
                    {title}
                  </h5>
                  <p className="gvisit-doc-modal__subtitle">{subtitle}</p>
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={saving}
              />
            </div>

            <form className="gvisit-doc-modal__form" onSubmit={handleSubmit}>
              <div className="gvisit-doc-modal__body">
                <section className="gvisit-doc-modal__section">
                  <div className="gvisit-doc-modal__field">
                    <label className="gvisit-doc-modal__label" htmlFor="gvisit-doc-file">
                      <span className="gvisit-doc-modal__label-text">
                        Choose file <span className="gvisit-doc-modal__req">*</span>
                      </span>
                      <span className="gvisit-doc-modal__label-hint">
                        PDF or Excel only · max {SCHEDULE_DOC_MAX_FILE_SIZE_MB} MB
                      </span>
                    </label>
                    <div
                      className={`gvisit-doc-modal__dropzone${
                        dragActive ? " is-dragover" : ""
                      }${file ? " has-file" : ""}${formError && !file ? " is-invalid" : ""}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <input
                        ref={fileInputRef}
                        id="gvisit-doc-file"
                        type="file"
                        className="gvisit-doc-modal__file-input"
                        accept={FILE_ACCEPT_ATTRIBUTE}
                        onChange={handleFileChange}
                        disabled={saving}
                      />
                      {file ? (
                        <div className="gvisit-doc-modal__file-card">
                          <div className="gvisit-doc-modal__file-icon" aria-hidden="true">
                            <i className="fas fa-file-alt" />
                          </div>
                          <div className="gvisit-doc-modal__file-meta">
                            <strong>{file.name}</strong>
                            <span>{formatFileSize(file.size)}</span>
                          </div>
                          <button
                            type="button"
                            className="gvisit-doc-modal__file-change"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={saving}
                          >
                            Change file
                          </button>
                        </div>
                      ) : (
                        <div className="gvisit-doc-modal__dropzone-placeholder">
                          <i className="fas fa-cloud-upload-alt" aria-hidden="true" />
                          <strong>Drag and drop or click to browse</strong>
                          <span>{SCHEDULE_DOC_ALLOWED_EXTENSIONS.join(", ")} up to {SCHEDULE_DOC_MAX_FILE_SIZE_MB} MB</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="gvisit-doc-modal__field">
                    <label className="gvisit-doc-modal__label" htmlFor="gvisit-doc-name">
                      <span className="gvisit-doc-modal__label-text">
                        Document name <span className="gvisit-doc-modal__req">*</span>
                      </span>
                    </label>
                    <input
                      id="gvisit-doc-name"
                      type="text"
                      className="form-control form-control-sm"
                      value={workSheetName}
                      onChange={(event) => {
                        nameTouchedRef.current = true;
                        const nextName = event.target.value;
                        setWorkSheetName(nextName);
                        if (
                          isDuplicateWorksheetName(nextName, existingRows, {
                            excludeStudyWorkSheetId: isReplace
                              ? replaceTarget?.studyWorkSheetId || null
                              : null,
                            excludeStudyWorkSheetGroupId: isReplace
                              ? replaceTarget?.studyWorkSheetGroupId || null
                              : null,
                          })
                        ) {
                          setFormError("A document with this name already exists for this visit.");
                        } else if (formError.includes("already exists")) {
                          setFormError("");
                        }
                      }}
                      disabled={saving}
                    />
                  </div>

                  {formError ? <p className="gvisit-doc-modal__error">{formError}</p> : null}
                </section>
              </div>

              <div className="gvisit-doc-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={saving || !workSheetName.trim() || !file}
                >
                  {saving ? "Saving…" : isReplace ? "Replace document" : "Save document"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </QmsModalPortal>
  );
}
